import type { RenderConfig } from "@memento/types/render-config";
import { type SetEditor } from "src/components/Renderer/RendererProvider";

export type UseGreetingsType = ReturnType<typeof useGreetings>;

export const useGreetings = (editor: RenderConfig, setEditor: SetEditor) => {
  function addNewGreeting(variant: "intro" | "outro") {
    const type = variant === "intro" ? "introGreeting" : "outroGreeting";

    setEditor((prev) => ({
      ...prev,
      [type]: {
        enabled: true,
        messages: [...getGreetings(variant), ""],
      },
    }));
  }

  function removeGreeting(variant: "intro" | "outro", index: number) {
    const type = variant === "intro" ? "introGreeting" : "outroGreeting";

    setEditor((prev) => ({
      ...prev,
      [type]: {
        enabled: true,
        messages: getGreetings(variant).filter((_, i) => i !== index),
      },
    }));
  }

  function removeEmptyGreetings(variant: "intro" | "outro") {
    const type = variant === "intro" ? "introGreeting" : "outroGreeting";

    const greetings = getGreetings(variant);
    const messages = greetings.filter((greeting) => greeting !== "");

    setEditor((prev) => {
      // if removing empty greetings results in no greetings, disable the greeting
      let enableGreetings = prev[type].enabled;
      if (messages.length === 0) {
        enableGreetings = false;
      }

      return {
        ...prev,
        [type]: {
          enabled: enableGreetings,
          messages,
        },
      };
    });
  }

  function updateGreeting(
    variant: "intro" | "outro",
    index: number,
    newValue: string
  ) {
    const type = variant === "intro" ? "introGreeting" : "outroGreeting";

    setEditor((prev) => ({
      ...prev,
      [type]: {
        enabled: true,
        messages: getGreetings(variant).map((oldValue, i) =>
          i === index ? newValue : oldValue
        ),
      },
    }));
  }

  function getGreetings(variant: "intro" | "outro") {
    const type = variant === "intro" ? "introGreeting" : "outroGreeting";

    return editor[type].messages;
  }

  function toggleGreetings(variant: "intro" | "outro") {
    const type = variant === "intro" ? "introGreeting" : "outroGreeting";

    setEditor((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        enabled: !prev[type].enabled,
      },
    }));
  }

  function getIsGreetingsEnabled(variant: "intro" | "outro") {
    const type = variant === "intro" ? "introGreeting" : "outroGreeting";

    return editor[type].enabled;
  }

  return {
    addNewGreeting,
    getGreetings,
    toggleGreetings,
    getIsGreetingsEnabled,
    removeGreeting,
    removeEmptyGreetings,
    updateGreeting,
  };
};
